import React, { useEffect, useId, useRef, useState } from 'react';
import { CheckCircle, Download, Loader2 } from 'lucide-react';
import { useInstallPrompt } from '../hooks/useInstallPrompt';

function InstallInstructions() {
  const { userAgent = '', platform, maxTouchPoints = 0 } = window.navigator;
  const isIOS = /iphone|ipad|ipod/i.test(userAgent) || (platform === 'MacIntel' && maxTouchPoints > 1);
  const isSafari = /safari/i.test(userAgent) && !/chrome|chromium|crios|edg|opr|android/i.test(userAgent);
  if (isIOS) return <>
    <ol className="list-decimal space-y-2 pl-5">
      <li>Open LeadMarka in Safari.</li>
      <li>Tap Share, then “Add to Home Screen”.</li>
      <li>If shown, turn on “Open as Web App”, then tap Add.</li>
    </ol>
    <p>You can then open LeadMarka from your home screen.</p>
  </>;
  if (isSafari && /mac/i.test(userAgent)) return <>
    <p>In Safari, choose File → Add to Dock, then Add.</p>
    <p>If Add to Dock is unavailable, try an up-to-date Safari, Chrome, or Edge.</p>
  </>;
  if (/android/i.test(userAgent)) return <>
    <p>Open your browser menu and look for “Install app” or “Add to Home screen”. Follow the browser’s instructions.</p>
    <p>If neither option appears, open LeadMarka in Chrome and try again.</p>
  </>;
  return <>
    <p>In Chrome or Edge, use the install icon in the address bar, or look in the browser menu for an option to install this page as an app.</p>
    <p>If no install option appears, you can keep using LeadMarka in your browser.</p>
  </>;
}

export default function InstallApp({ showStatus = false, className = '' }) {
  const { canInstall, isInstalled, isStandalone, installing, promptInstall } = useInstallPrompt();
  const [showInstructions, setShowInstructions] = useState(false);
  const [error, setError] = useState('');
  const dialogRef = useRef(null);
  const triggerRef = useRef(null);
  const titleId = useId();
  const installed = isInstalled || isStandalone;

  useEffect(() => {
    if (showInstructions && !installed) dialogRef.current?.showModal();
  }, [showInstructions, installed]);

  const closeInstructions = () => {
    dialogRef.current?.close();
    setShowInstructions(false);
    triggerRef.current?.focus();
  };

  const install = async () => {
    if (installing) return;
    setError('');
    if (!canInstall) {
      setShowInstructions(true);
      return;
    }
    try {
      await promptInstall();
    } catch {
      setError('The install prompt could not open. Use the browser instructions below to install LeadMarka.');
      setShowInstructions(true);
    }
  };

  if (installed) return showStatus ? (
    <p role="status" className="flex items-center gap-2 rounded-lg border border-green-200 bg-green-50 p-3 text-sm text-green-700">
      <CheckCircle aria-hidden="true" className="h-5 w-5" />
      {isStandalone ? 'You’re using the installed LeadMarka app.' : 'LeadMarka was installed on this device.'}
    </p>
  ) : null;

  return <>
    <button ref={triggerRef} type="button" onClick={install} disabled={installing}
      className={`flex w-full items-center justify-center gap-2 rounded-lg px-3 py-3 text-sm font-medium text-primary-700 hover:bg-primary-50 disabled:opacity-60 ${className}`}>
      {installing ? <Loader2 aria-hidden="true" className="h-4 w-4 animate-spin" /> : <Download aria-hidden="true" className="h-4 w-4" />}
      {installing ? 'Opening install…' : 'Install LeadMarka'}
    </button>
    {showInstructions && <dialog ref={dialogRef} aria-labelledby={titleId} onCancel={(event) => { event.preventDefault(); closeInstructions(); }}
      className="m-auto w-[calc(100%-2rem)] max-w-md rounded-2xl border border-gray-200 bg-white p-6 text-gray-900 shadow-xl backdrop:bg-black/40">
      <h2 id={titleId} className="text-lg font-semibold">Install LeadMarka</h2>
      <div className="mt-3 space-y-3 text-sm leading-6 text-gray-600">
        {error && <p role="alert" className="text-red-700">{error}</p>}
        <InstallInstructions />
        <p>Installation is optional. Signing in and saving changes require an internet connection.</p>
      </div>
      <button type="button" onClick={closeInstructions} className="mt-5 w-full rounded-lg bg-primary-600 px-4 py-3 text-sm font-semibold text-white hover:bg-primary-700">Close</button>
    </dialog>}
  </>;
}
